import React from 'react';
import {
  Button,
  Callout,
  Card,
  Checkbox,
  Dialog,
  FormGroup,
  InputGroup,
  Intent,
  NonIdealState,
  Tag,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Page } from '../../utils/Layout/Layout';
import { getAuthToken } from '../auth';
import './UserSettings.css';

const DEFAULT_SCOPES = 'torga:read torga:write';

async function api(path, options = {}) {
  const token = getAuthToken();
  const res = await fetch(path, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const err = new Error(
      typeof data === 'object' && data?.error
        ? String(data.error)
        : res.statusText
    );
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

export default function UserSettings() {
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [name, setName] = React.useState('');
  const [scopes, setScopes] = React.useState(DEFAULT_SCOPES);
  const [secretModal, setSecretModal] = React.useState(null);

  const tokenUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/api/auth/oauth/token`
      : '/api/auth/oauth/token';

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api('/api/v3/app/user/oauth-clients');
      setClients(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message || String(e));
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(e) {
    e.preventDefault();
    setError(null);
    try {
      const created = await api('/api/v3/app/user/oauth-clients', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim() || 'API Client',
          scopes: scopes.trim() || DEFAULT_SCOPES,
        }),
      });
      setSecretModal({
        title: 'Save your client secret',
        client_id: created.client_id,
        client_secret: created.client_secret,
      });
      setName('');
      setScopes(DEFAULT_SCOPES);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function rotate(id) {
    if (
      !window.confirm(
        'Rotate secret? The old secret stops working immediately.'
      )
    ) {
      return;
    }
    setError(null);
    try {
      const data = await api(
        `/api/v3/app/user/oauth-clients/${id}/rotate-secret`,
        { method: 'POST', body: '{}' }
      );
      setSecretModal({
        title: 'New client secret',
        client_id: data.client_id,
        client_secret: data.client_secret,
      });
      await load();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function setEnabled(id, enabled) {
    setError(null);
    try {
      await api(`/api/v3/app/user/oauth-clients/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      });
      await load();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  async function remove(id) {
    if (!window.confirm('Revoke this API client? This cannot be undone.')) {
      return;
    }
    setError(null);
    try {
      await api(`/api/v3/app/user/oauth-clients/${id}`, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message || String(e));
    }
  }

  return (
    <Page>
      <h1>User settings</h1>

      <Callout intent={Intent.PRIMARY} icon={IconNames.INFO_SIGN}>
        <strong>OAuth2 client credentials</strong> for MCP and API access. Use
        the token URL below with <code>grant_type=client_credentials</code>,
        your <code>client_id</code>, and <code>client_secret</code>. Scopes:{' '}
        <code>torga:read</code>, <code>torga:write</code>, optional{' '}
        <code>mcp:tools</code> (full automation).
      </Callout>

      <p className="user-settings__token-url">
        Token URL: <code>{tokenUrl}</code>
      </p>

      {error && (
        <Callout intent={Intent.DANGER} style={{ marginTop: 12 }}>
          {error}
        </Callout>
      )}

      <Card elevation={1} style={{ marginTop: 16 }}>
        <h3>Create API client</h3>
        <form onSubmit={handleCreate}>
          <FormGroup label="Display name">
            <InputGroup
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              placeholder="e.g. Cursor MCP"
            />
          </FormGroup>
          <FormGroup
            label="Scopes (space-separated)"
            helperText="Default is read + write on the Torga API."
          >
            <InputGroup
              value={scopes}
              onChange={(ev) => setScopes(ev.target.value)}
            />
          </FormGroup>
          <Button intent={Intent.PRIMARY} type="submit" icon={IconNames.PLUS}>
            Create client
          </Button>
        </form>
      </Card>

      <Card elevation={1} style={{ marginTop: 16 }}>
        <h3>Your API clients</h3>
        {loading ? (
          <NonIdealState icon={IconNames.CIRCLE} title="Loading..." />
        ) : clients.length === 0 ? (
          <p>No API clients yet.</p>
        ) : (
          <ul className="user-settings__client-list">
            {clients.map((c) => (
              <li key={c.id} className="user-settings__client-item">
                <div>
                  <strong>{c.name}</strong>{' '}
                  <code className="user-settings__mono">{c.client_id}</code>{' '}
                  {c.enabled ? (
                    <Tag intent={Intent.SUCCESS} minimal>
                      enabled
                    </Tag>
                  ) : (
                    <Tag intent={Intent.WARNING} minimal>
                      disabled
                    </Tag>
                  )}
                </div>
                <div className="user-settings__scopes">
                  <small>{c.scopes}</small>
                </div>
                <div className="user-settings__actions">
                  <Button
                    small
                    minimal
                    icon={IconNames.REPEAT}
                    onClick={() => rotate(c.id)}
                  >
                    Rotate secret
                  </Button>
                  <Checkbox
                    checked={c.enabled}
                    label="Enabled"
                    onChange={(ev) => setEnabled(c.id, ev.target.checked)}
                  />
                  <Button
                    small
                    minimal
                    intent={Intent.DANGER}
                    icon={IconNames.TRASH}
                    onClick={() => remove(c.id)}
                  >
                    Revoke
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog
        isOpen={Boolean(secretModal)}
        onClose={() => setSecretModal(null)}
        title={secretModal?.title || 'Secret'}
        canOutsideClickClose={false}
      >
        <div className="bp5-dialog-body">
          <Callout intent={Intent.WARNING} icon={IconNames.WARNING_SIGN}>
            Copy this secret now. It will not be shown again.
          </Callout>
          {secretModal && (
            <>
              <FormGroup label="Client ID" style={{ marginTop: 12 }}>
                <InputGroup
                  readOnly
                  value={secretModal.client_id}
                  rightElement={
                    <Button
                      minimal
                      icon={IconNames.DUPLICATE}
                      onClick={() =>
                        navigator.clipboard.writeText(secretModal.client_id)
                      }
                    />
                  }
                />
              </FormGroup>
              <FormGroup label="Client secret">
                <InputGroup
                  readOnly
                  value={secretModal.client_secret}
                  rightElement={
                    <Button
                      minimal
                      icon={IconNames.DUPLICATE}
                      onClick={() =>
                        navigator.clipboard.writeText(
                          secretModal.client_secret
                        )
                      }
                    />
                  }
                />
              </FormGroup>
            </>
          )}
        </div>
        <div className="bp5-dialog-footer">
          <Button intent={Intent.PRIMARY} onClick={() => setSecretModal(null)}>
            Done
          </Button>
        </div>
      </Dialog>
    </Page>
  );
}
